// The Gantt's timeline, shared: the month and day (or week, or month) scale, weekends, today's line and the bars, as
// the Productivity Gantt draws them. GanttView uses the scale; MiniGantt is the read-only version for a page that shows
// how something moves through its steps (an Implementation Factory request: Define, Design, Build, Test, Deploy,
// Measure), with what's done, where it is today and what's expected.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { addDays, daysBetween, todayIso } from "./format";
import "./productivity.css";

export type Zoom = "day" | "week" | "month";
export const SCALE: Record<Zoom, number> = { day: 30, week: 12, month: 4 };

/** The scale across the top: a label per month (per year when zoomed out) and a tick per day, Monday or month. */
export function timeScale(r0: string, total: number, zoom: Zoom, px: number) {
  const ticks: { x: number; label: string; weekend?: boolean }[] = [];
  const months: { x: number; w: number; label: string }[] = [];
  for (let i = 0; i < total; i++) {
    const d = addDays(r0, i), dt = new Date(d + "T00:00:00Z"), dow = dt.getUTCDay(), dom = dt.getUTCDate();
    if (i === 0 || dom === 1) {
      if (months.length) months[months.length - 1].w = i * px - months[months.length - 1].x;
      months.push({ x: i * px, w: 0, label: dt.toLocaleDateString("en-US", zoom === "month" ? { year: "numeric", timeZone: "UTC" } : { month: "long", year: "numeric", timeZone: "UTC" }) });
    }
    if (zoom === "day") ticks.push({ x: i * px, label: String(dom), weekend: dow === 0 || dow === 6 });
    else if (zoom === "week" && dow === 1) ticks.push({ x: i * px, label: String(dom) });
    else if (zoom === "month" && dom === 1) ticks.push({ x: i * px, label: dt.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }) });
  }
  if (months.length) months[months.length - 1].w = total * px - months[months.length - 1].x;
  // Months view: one segment a year is enough.
  const merged = zoom === "month" ? months.reduce<typeof months>((acc, m) => { const last = acc[acc.length - 1]; if (last && last.label === m.label) last.w += m.w; else acc.push({ ...m }); return acc; }, []) : months;
  return { ticks, months: merged };
}

/** One bar: done (as the Gantt draws a task), where it is now (solid), or expected (outlined). */
export type MiniSpan = { start: string; end: string; kind: "done" | "current" | "expected"; title?: string };
export type MiniRow = {
  key: string; label: ReactNode; href?: string; sub?: boolean; current?: boolean;
  spans: MiniSpan[];
  milestones?: { date: string; label: string; done: boolean }[];
  /** Text after the last bar, like the Gantt's task names. */
  note?: string;
};

const RH = 34;
const fmt = (d: string) => new Date(d + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** A read-only Gantt: names on the left, the timeline on the right, opened on today. Zooms out to weeks or months when
 *  the range is long, the way the Gantt does. */
export function MiniGantt({ rows, label = "Step", empty }: { rows: MiniRow[]; label?: string; empty?: ReactNode }) {
  const right = useRef<HTMLDivElement | null>(null);
  const today = todayIso();
  // The timeline fills the card: as wide as the space it has, wider (and scrolling) when the dates need it.
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = right.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 640)); ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps
  const { r0, total, zoom, px } = useMemo(() => {
    let lo = today, hi = today;
    for (const r of rows) {
      for (const s of r.spans) { if (s.start < lo) lo = s.start; if (s.end > hi) hi = s.end; }
      for (const m of r.milestones ?? []) { if (m.date < lo) lo = m.date; if (m.date > hi) hi = m.date; }
    }
    const days = daysBetween(lo, hi) + 1;
    const zoom: Zoom = days <= 45 ? "day" : days <= 200 ? "week" : "month";
    const px = zoom === "day" ? 26 : SCALE[zoom];
    const pad = zoom === "day" ? 2 : zoom === "week" ? 7 : 20;
    let a = addDays(lo, -pad);
    if (zoom !== "day") a = addDays(a, -((new Date(a + "T00:00:00Z").getUTCDay() + 6) % 7)); // start on a Monday
    let b = addDays(hi, pad * 2);
    if (daysBetween(a, b) < width / px) b = addDays(a, Math.ceil(width / px));
    return { r0: a, total: daysBetween(a, b) + 1, zoom, px };
  }, [rows, today, width]);
  const xOf = (d: string) => daysBetween(r0, d) * px;
  const { ticks, months } = useMemo(() => timeScale(r0, total, zoom, px), [r0, total, zoom, px]);
  // Open with today in view, a little in from the left.
  useEffect(() => { if (right.current) right.current.scrollLeft = Math.max(0, xOf(today) - right.current.clientWidth * 0.45); }, [r0, total]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows.length) return <div className="empty">{empty ?? "Nothing to show yet."}</div>;
  return (
    <div className="mg">
      <div className="mg-left">
        <div className="mg-h">{label}</div>
        {rows.map((r) => (
          <div key={r.key} className={`mg-name${r.sub ? " sub" : ""}${r.current ? " cur" : ""}`} title={typeof r.label === "string" ? r.label : undefined}>
            {r.href ? <Link to={r.href}>{r.label}</Link> : r.label}
          </div>
        ))}
      </div>
      <div className="mg-right" ref={right}>
        <div className="pv-tl" style={{ width: total * px, height: 34 + rows.length * RH }}>
          <div className="pv-tl-head">
            {months.map((m, i) => <div key={i} className="pv-tl-month" style={{ left: m.x, width: m.w }}><span>{m.label}</span></div>)}
            {ticks.map((t, i) => <div key={i} className={`pv-tl-tick${t.weekend ? " we" : ""}`} style={{ left: t.x, width: zoom === "day" ? px : undefined }}>{t.label}</div>)}
          </div>
          <div className="pv-tl-body" style={{ height: rows.length * RH }}>
            {zoom === "day" && ticks.filter((t) => t.weekend).map((t, i) => <div key={i} className="pv-tl-we" style={{ left: t.x, width: px }} />)}
            {ticks.map((t, i) => <div key={`l${i}`} className="pv-tl-line" style={{ left: t.x }} />)}
            <div className="pv-tl-today" style={{ left: xOf(today) + (zoom === "day" ? px / 2 : 0) }} title={`Today, ${fmt(today)}`} />
            {rows.map((_, i) => <div key={i} className="pv-tl-row" style={{ top: i * RH }} />)}
            {rows.map((r, i) => {
              const y = i * RH;
              const last = [...r.spans].sort((a, b) => b.end.localeCompare(a.end))[0];
              return (
                <div key={r.key}>
                  {r.spans.map((s, k) => {
                    const x = xOf(s.start), w = Math.max(px * 0.6, (daysBetween(s.start, s.end) + 1) * px);
                    const what = s.kind === "done" ? "" : s.kind === "current" ? " (now)" : " (expected)";
                    return <div key={k} className={`pv-bar mg-bar ${s.kind}${r.sub ? " sub" : ""}`} style={{ left: x, width: w, top: y + (r.sub ? 10 : 7) }}
                      title={`${s.title ?? (typeof r.label === "string" ? r.label : "")}${what}: ${fmt(s.start)}${s.end !== s.start ? ` to ${fmt(s.end)}` : ""}`} />;
                  })}
                  {(r.milestones ?? []).map((m, k) => (
                    <div key={`m${k}`} className={`pv-ms mg-ms${m.done ? "" : " todo"}`} style={{ left: xOf(m.date) + px / 2 - 6, top: y + RH / 2 - 6 }} title={`${m.label}: ${fmt(m.date)}${m.done ? "" : " (expected)"}`} />
                  ))}
                  {r.note && last && <span className="pv-bar-lab mg-note" style={{ left: xOf(last.end) + px + 8, top: y + RH / 2 }}>{r.note}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MiniGanttKey() {
  return (
    <span className="mg-key" aria-hidden="true">
      <span><i className="mg-k done" />Done</span><span><i className="mg-k current" />Now</span><span><i className="mg-k expected" />Expected</span><span><i className="mg-k ms" />Milestone</span>
    </span>
  );
}
